import { routeFileSelector } from '@/utilz';
import EmptyRecords from '@/views/components/Common/EmptyRecords';
import ScrollPagination from '@/views/components/Paginations/ScrollPagination';
import { Col, Divider, Row } from 'antd';
import { cloneDeep, maxBy } from 'es-toolkit';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  getLADClaimNotesInProgress,
  updateLossAndDamageClaimDetailsInProgress,
} from '../../../../../../redux/Claims/LossAndDamage/LossAndDamageClaimsActions';
import { LADCovertDatesFromMomentToAPIType } from '../../../../../../utilz/PreClaimsUtils';
import Button from '../../../../../components/Buttons/Button';
import PrimaryLabel from '../../../../../components/Labels/PrimaryLabel';
import PreClaimsNotesCard from '../../../PreClaims/CreateTabs/Tabs/Notes/PreClaimsNotesCard';

const LADClaimsNotesTab = () => {
  const [pageIndex, setPageIndex] = useState(1);
  const dispatch = useDispatch();
  const [notesList, setNotesList] = useState([]);
  const claimDetails = useSelector((state) => state.lossAndDamage.claimDetails);
  const claimNotes = useSelector((state) => state.lossAndDamage.claimNotes);
  const isNewRecordSubmitting = useRef(false);
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);

  useEffect(() => {
    if (claimNotes?.data?.length > 0) {
      // To prevent new data to be deleted
      let newItem = false;
      if (!isNewRecordSubmitting.current) {
        newItem = notesList?.find((item) => item.isNew);
      }
      const notes = claimNotes.data.map((row) => ({
        ...row,
        isNew: false,
      }));
      setNotesList([...(newItem ? [newItem] : []), ...notes]);
      isNewRecordSubmitting.current = false;
    }
  }, [claimNotes]);

  const handleSubmit = (data) => {
    let payload = cloneDeep(claimDetails?.data);
    const noteDetails = { ...data, dirtyFlag: data.isNew ? 'I' : 'U' };
    payload = LADCovertDatesFromMomentToAPIType(payload);
    payload.claimComment = [noteDetails];
    payload.section = 'COMMENTS';
    payload.pageIndex = pageIndex;
    isNewRecordSubmitting.current = data.isNew;
    dispatch(updateLossAndDamageClaimDetailsInProgress(payload));
  };

  const setEditMode = (index, val) => {
    const notesListLocal = cloneDeep(notesList);
    notesListLocal[index].editMode = val;
    setNotesList(notesListLocal);
  };

  const handleDelete = (index, isNew) => () => {
    if (isNew) {
      const notesLocal = cloneDeep(notesList);
      notesLocal.splice(index, 1);
      setNotesList(notesLocal);
    } else {
      let payload = cloneDeep(claimDetails?.data);
      const noteDetails = { ...notesList[index], dirtyFlag: 'D' };
      payload = LADCovertDatesFromMomentToAPIType(payload);
      payload.claimComment = [noteDetails];
      payload.section = 'COMMENTS';
      payload.pageIndex = pageIndex;
      dispatch(updateLossAndDamageClaimDetailsInProgress(payload));
    }
  };

  const handleCancel = (index, isNew) => () => {
    if (isNew) {
      const notesLocal = cloneDeep(notesList);
      notesLocal.splice(index, 1);
      setNotesList(notesLocal);
    } else {
      const notesLocal = cloneDeep(notesList);
      notesLocal[index] = claimDetails?.data?.claimComment?.[index];
      setNotesList(notesLocal);
    }
  };

  const handleAddNew = () => () => {
    let notesListLocal = cloneDeep(notesList);
    let nextSequence = maxBy(notesListLocal, ({ sequenceNo }) => Number(sequenceNo));
    nextSequence = nextSequence ? nextSequence.sequenceNo : 0;
    notesListLocal = [
      {
        claimKey: claimDetails?.data?.claimKey,
        commentType: undefined,
        commentText: undefined,
        sequenceNo: 0,
        oldCommentType: null,
        dirtyFlag: 'i',
        isNew: true,
        editMode: true,
      },
      ...notesListLocal,
    ];
    setNotesList(notesListLocal);
  };

  useEffect(() => {
    if (claimDetails.data?.claimKey) {
      dispatch(
        getLADClaimNotesInProgress({
          claimKey: claimDetails.data.claimKey,
          pageIndex,
        }),
      );
    }
  }, [claimDetails.data?.claimKey]);

  const handleNextPage = () => {
    if (claimDetails.data?.claimKey) {
      const nextPageNo = pageIndex + 1;
      dispatch(
        getLADClaimNotesInProgress({
          claimKey: claimDetails.data.claimKey,
          pageIndex: nextPageNo,
        }),
      );
      setPageIndex(nextPageNo);
    }
  };

  return (
    <div className="h-100 d-flex flex-direction-column">
      <div className="py-10 px-30">
        <Row justify="space-between" align="middle">
          <Col>
            <PrimaryLabel>Notes</PrimaryLabel>
          </Col>
          <Col>
            <Button variant="success" dataTestId="add-new-button" onClick={handleAddNew()}>
              Add New
            </Button>
          </Col>
        </Row>
      </div>
      <div>
        <Divider className="m-0 border-bottom-border" />
      </div>
      {notesList.length > 0 || claimNotes.progressing ? (
        <ScrollPagination
          className="px-30 py-10 flex-1"
          loadMore={handleNextPage}
          isLoading={claimNotes.progressing}
          hasMore={claimNotes.data?.length < claimNotes.totalRows}
          scrollOffset={30}
        >
          {notesList.map((row, index) => (
            <PreClaimsNotesCard
              noteDetails={row}
              key={row.sequenceNo}
              index={index}
              editMode={Boolean(row.editMode)}
              setEditMode={setEditMode}
              handleCancel={handleCancel}
              handleSubmit={handleSubmit}
              handleDelete={handleDelete}
            />
          ))}
        </ScrollPagination>
      ) : (
        <EmptyRecords
          title={t('translation:default.documentManager.noFoundText')}
          descriptionOne={t('translation:claims.emptyClaimNotes')}
          descriptionTwo={''}
        />
      )}
    </div>
  );
};

export default LADClaimsNotesTab;
